import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../api/client';
import { Bandeau, Carte, Kpi, Squelette, Vide } from '../components/ui';
import { dateFr, montant } from '../lib/format';
import { T } from '../i18n';

interface Ligne {
  contratId: string; numero: string; reservationId: string | null; reservationNumero: string | null;
  client: string; telephone: string | null;
  vehicule: string; immatriculation: string | null;
  reglement: string; statutContrat: string; annulee?: boolean;
  rendue: boolean; nonRestituee: boolean; commencee: boolean;
  debut: string | null; fin: string | null; age: number;
  totalMinor: number; encaisseMinor: number; duMinor: number;
}
interface Reponse {
  lignes: Ligne[];
  totalDuMinor: number; aCreditMinor: number; nonEncaisseMinor: number;
  nombreRendues: number; nombreNonRestituees: number; nombreAnciennes: number;
  seuilAlerteJours: number;
}

type Filtre = 'tout' | 'credit' | 'oubli' | 'rendue';

const FILTRES = (): { cle: Filtre; label: string }[] => [
  { cle: 'tout', label: T.dettes.filtres.tout },
  { cle: 'credit', label: T.dettes.filtres.credit },
  { cle: 'oubli', label: T.dettes.filtres.oubli },
  { cle: 'rendue', label: T.dettes.filtres.rendue },
];

/**
 * Ce que les clients doivent.
 *
 * L'ecran d'accueil ne montrait que les contrats ACTIFS : une voiture rendue
 * dont le solde restait du sortait de tous les ecrans, et l'argent avec elle.
 * Ici rien ne disparait tant que le solde n'est pas a zero.
 *
 * Le montant vient du solde du contrat, recalcule depuis les mouvements de
 * caisse : aucune dette ne peut differer de ce que dit la caisse.
 */
export default function Dettes() {
  const [d, setD] = useState<Reponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [filtre, setFiltre] = useState<Filtre>('tout');

  useEffect(() => {
    api.get<Reponse>('/dettes').then(setD).catch((e) => setErreur(
      e instanceof ApiError ? e.erreur : T.dettes.erreurChargement,
    ));
  }, []);

  /* Un ecran qui echoue doit le DIRE : un squelette gris eternel laisse croire
   * que ca charge encore, et personne ne sait quoi faire. */
  const t = T.dettes;
  if (erreur) return <Bandeau type="ko" titre={t.erreurTitre}>{erreur}</Bandeau>;
  if (!d) return <Squelette lignes={6} />;

  const visibles = d.lignes.filter((l) => (
    filtre === 'credit' ? (l.reglement === 'differe' || l.reglement === 'par_tranche')
      : filtre === 'oubli' ? (l.reglement !== 'differe' && l.reglement !== 'par_tranche')
        : filtre === 'rendue' ? (l.rendue || l.nonRestituee)
          : true));

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{t.titre}</h1>
          <p>{t.nonSoldes(d.lignes.length)}</p>
        </div>
      </div>

      {d.nombreNonRestituees > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="ko" titre={t.nonRestituees.titre(d.nombreNonRestituees)}>
            {t.nonRestituees.texte}
          </Bandeau>
        </div>
      )}
      {d.nombreRendues > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="ko" titre={t.rendues.titre(d.nombreRendues)}>
            {t.rendues.texte}
          </Bandeau>
        </div>
      )}
      {d.nombreAnciennes > 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={t.anciennes.titre(d.nombreAnciennes, d.seuilAlerteJours)}>
            {t.anciennes.texte}
          </Bandeau>
        </div>
      )}

      <div className="grille g3" style={{ marginBottom: 16 }}>
        <Kpi libelle={t.kpi.total} valeur={montant(d.totalDuMinor)}
             detail={t.kpi.totalDetail(d.lignes.length)} className="signature-kpi" />
        <Kpi libelle={t.kpi.credit} valeur={montant(d.aCreditMinor)}
             detail={t.kpi.creditDetail} />
        <Kpi libelle={t.kpi.nonEncaisse} valeur={montant(d.nonEncaisseMinor)}
             detail={t.kpi.nonEncaisseDetail} />
      </div>

      <Carte pad={false} action={
        <div className="actions">
          {FILTRES().map((f) => (
            <button key={f.cle} className={`btn btn-s ${filtre === f.cle ? 'btn-p' : ''}`}
                    onClick={() => setFiltre(f.cle)}>{f.label}</button>
          ))}
        </div>
      } titre={t.quiDoitQuoi}>
        {visibles.length === 0 ? (
          <div className="carte-p">
            <Vide titre={d.lignes.length === 0 ? t.videAucun : t.videFiltre} />
          </div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t.colonnes.client}</th><th>{t.colonnes.voiture}</th><th>{t.colonnes.dossier}</th>
                  <th>{t.colonnes.fin}</th><th className="droite">{t.colonnes.total}</th>
                  <th className="droite">{t.colonnes.encaisse}</th><th className="droite">{t.colonnes.resteDu}</th><th />
                </tr>
              </thead>
              <tbody>
                {visibles.map((l) => (
                  <tr key={l.contratId}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <b>{l.client}</b>
                      {l.telephone && <div className="mini sourdine num">{l.telephone}</div>}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {l.vehicule}
                      {l.immatriculation && <div className="mini sourdine num">{l.immatriculation}</div>}
                    </td>
                    <td className="mini">
                      {/* La dette mene au dossier : c'est la qu'on voit les dates,
                        * les supplements et l'etat des lieux avant de relancer. */}
                      {l.reservationId
                        ? <Link to={`/reservations/${l.reservationId}`}>{l.numero}</Link>
                        : l.numero}
                      <div className="sourdine">
                        {l.annulee ? t.etat.annulee
                          : l.reglement === 'differe' ? t.etat.credit
                            : l.reglement === 'par_tranche' ? t.etat.tranches
                              : l.commencee ? t.etat.nonEncaissee : t.etat.aVenir}
                        {l.nonRestituee ? t.etat.nonRendue
                          : l.rendue ? t.etat.rendue : ''}
                        {l.statutContrat === 'clos' && t.etat.clos}
                      </div>
                    </td>
                    <td className="mini">
                      {l.fin ? dateFr(l.fin) : '—'}
                      <div className={l.age > d.seuilAlerteJours ? 'mini' : 'mini sourdine'}
                           style={l.age > d.seuilAlerteJours ? { color: 'var(--danger)', fontWeight: 600 } : undefined}>
                        {l.age > 0 ? t.retard(l.age)
                          : l.age === 0 ? t.echueAujourdhui : t.enCours}
                      </div>
                    </td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                    <td className="droite montant sourdine">{montant(l.encaisseMinor)}</td>
                    <td className="droite montant" style={{ color: 'var(--danger)', fontWeight: 700 }}>
                      {montant(l.duMinor)}
                    </td>
                    <td className="droite">
                      <Link className="btn btn-s" to={`/caisse?contrat=${l.contratId}`}>{t.encaisser}</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 780 }}>
        {t.note}
      </p>
    </>
  );
}
